import { useState } from 'react';
import { LOCALES, type Locale } from '@genoma/shared';

import { useI18n } from '../i18n/index.tsx';
import { setMyLocale } from '../services/auth.service.ts';
import { useAuthStore } from '../stores/auth.store.ts';

/**
 * The language this person reads in.
 *
 * Stored on the person rather than in the browser, so signing in from another
 * machine does not put them back into a language they did not choose. Before
 * this existed the interface followed `navigator.language`, which meant two
 * colleagues at the same company saw two different applications and neither
 * had picked one.
 *
 * Applied immediately and saved in the background: the switch is a preference,
 * not a transaction, and making somebody wait for a round trip to see their own
 * language change would be strange. If the save fails they still get the
 * language; the next session will simply ask again.
 */
const LABELS: Record<Locale, string> = { es: 'Español', en: 'English' };

export const LanguageSwitch = () => {
  const { locale, setLocale } = useI18n();
  const user = useAuthStore((state) => state.user);
  const [saving, setSaving] = useState(false);

  const change = (next: Locale) => {
    setLocale(next);
    if (!user) return;
    setSaving(true);
    void setMyLocale(next)
      .catch(() => undefined)
      .finally(() => setSaving(false));
  };

  return (
    <label className="mt-3 flex items-center gap-2 px-1">
      <span className="sr-only">{LABELS[locale]}</span>
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" aria-hidden className="size-4 shrink-0 stroke-white/40">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
      </svg>
      <select
        value={locale}
        disabled={saving}
        onChange={(event) => change(event.target.value as Locale)}
        className="w-full cursor-pointer rounded-lg border border-white/10 bg-transparent px-2 py-1.5 text-[12.5px] text-white/70 outline-none hover:border-white/25 focus:border-white/40"
      >
        {LOCALES.map((option) => (
          // Each option in its own language: somebody stuck in the wrong one
          // needs to recognise the way out without being able to read the rest.
          <option key={option} value={option} className="bg-[#1b1c1d] text-white">
            {LABELS[option]}
          </option>
        ))}
      </select>
    </label>
  );
};
